"use client";

import { useRouter } from 'next/navigation';
import React from 'react';
import type { ApiIndexData, MarketIndicesListProps } from '../../interfaces/MarketToday.interface';
import { handleRedirection } from '../../utils/helper';

const MarketIndicesList: React.FC<MarketIndicesListProps> = ({ data, handleClick }) => {
  const router = useRouter();

  const getNormalizedUrl = (redirectURL?: string) => {
    if (!redirectURL) return "";
    return redirectURL.startsWith("/") ? redirectURL : `/${redirectURL}`;
  };

  const navigateToIndex = (index: ApiIndexData, event?: React.SyntheticEvent) => {
    if (!index.redirectURL) return;

    const normalizedUrl = getNormalizedUrl(index.redirectURL);

    if (event) {
      handleClick(event as unknown as React.MouseEvent<HTMLElement>, index);
    }

    handleRedirection(normalizedUrl, router);
  };

  return (
    <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 mb-3 ">
      {data?.indicesList?.map((index) => {
        const isPositive = parseFloat(index.changePrice) >= 0;
        const normalizedUrl = getNormalizedUrl(index.redirectURL);

        return (
          <button
            key={index.symbol}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToIndex(index, e);
            }}
            aria-label={`View ${index.symbol} index details`}
            className="appearance-none shrink-0 w-[138px] md:w-[160px] bg-white rounded-[8px] p-2 border border-[#F2F2F2] h-[86px] md:h-[97px] flex flex-col cursor-pointer text-left no-underline focus:outline-none"
            data-href={normalizedUrl}
          >
            <h2 className="text-xs md:text-base font-medium h-[19px] mb-3 md:mb-4 text-[#000000]">
              {index.symbol}
            </h2>
            
            <div className="flex items-center gap-1 mb-1">
              <span className={`text-sm md:text-base ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {index.ltp}
              </span>
              {isPositive ? (
                <span className="text-xs text-green-600 w-3 h-3 leading-none flex items-center justify-center">▲</span>
              ) : (
                <span className="text-xs text-red-600 w-3 h-3 leading-none flex items-center justify-center">▼</span>
              )}
            </div>
            
            <p className="text-[#666] min-h-[16px] text-xs">
              {index.changePrice} ({index.perChange}%)
            </p>
          </button>
        );
      })}
    </div>
  );
};

export default MarketIndicesList;
